import { Loader } from '@fountain/fountain-ui-components';
import { Grid, makeStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { makeSelectAccountId } from 'containers/Auth_old/selectors';
import { AxAgentValues, ListedAxAgent } from 'containers/ChatAgent/types';
import { addMessageAction } from 'containers/FlashMessage/actions';

import { fetchAccessToken, fetchAgentSetupList } from '../api';
import { initialAxAgentValues } from '../constants';
import { messages } from '../messages';
import { AgentList } from './AgentList';
import { ChatbotProgressor } from './ChatbotProgressor';
import { DisplayHeightContext } from './contexts/display-height-context';
import { SetupInstructions } from './SetupInstructions';

const useStyles = makeStyles({
  progressorWrapper: {
    height: '100%',
    width: '100%',
  },
});

interface ChatbotSetupProps {
  startChatbotProgressor: boolean;
  setStartChatbotProgressor: React.Dispatch<React.SetStateAction<boolean>>;
  accountSlug: string;
}

export const ChatbotSetup: VFC<ChatbotSetupProps> = ({
  startChatbotProgressor,
  setStartChatbotProgressor,
  accountSlug,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [refreshToken, setRefreshToken] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState<ListedAxAgent[]>([]);
  const [agentsIncomplete, setAgentsIncomplete] = useState(false);
  const [axAgentValues, setAxAgentValues] =
    useState<AxAgentValues>(initialAxAgentValues);
  const [displayHeight, setDisplayHeight] = useState<number>(0);
  const accountExternalId = useSelector(makeSelectAccountId());
  const listRequestIdRef = useRef(0);

  const measuredRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver(() => {
        const { height } = node.getBoundingClientRect();
        setDisplayHeight(height);
      });
      resizeObserver.observe(node);
    }
  }, []);

  useEffect(() => {
    // The tenant token never persists in the browser, so drop any copy left
    // under the legacy key, where a later user of the browser could read it.
    try {
      localStorage.removeItem(`${accountExternalId}-${accountSlug}-token`);
    } catch {
      // Storage can be unavailable; nothing to clean up then.
    }
  }, [accountExternalId, accountSlug]);

  useEffect(() => {
    if (startChatbotProgressor) return;

    listRequestIdRef.current += 1;
    const requestId = listRequestIdRef.current;
    const fetchList = async () => {
      setIsLoading(true);
      try {
        const list = await fetchAgentSetupList(accountSlug);
        if (listRequestIdRef.current !== requestId) return;

        setAgents(list.agents);
        setAgentsIncomplete(list.agentsIncomplete);
        setAxAgentValues(prev => ({
          ...prev,
          sampleKnowledgeBaseFileUrl: list.sampleKnowledgeBaseFileUrl,
          knowledgeBaseHelpCenterArticleUrl:
            list.knowledgeBaseHelpCenterArticleUrl,
          totalChatAgents: list.agents.length,
          availableAgents: list.availableAgents,
        }));
      } catch {
        if (listRequestIdRef.current !== requestId) return;
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.fetchAgentsFailed),
            'error',
          ),
        );
      } finally {
        if (listRequestIdRef.current === requestId) setIsLoading(false);
      }
    };

    void fetchList();
  }, [accountSlug, dispatch, intl, refreshToken, startChatbotProgressor]);

  // The tenant token reads every applicant transcript of the account, and Hire
  // issues it only to account admins. Each setup action asks for a fresh one,
  // keeps it in memory only, and writes the fountain-ai URLs that Widget,
  // EmbedCodeModal and the API helpers read before anything reads them.
  const loadSetupToken = useCallback(async (): Promise<string | null> => {
    try {
      const data = await fetchAccessToken(accountSlug);
      localStorage.setItem(
        'chatAgentBackendUrl',
        data.chat_agent_connection_url,
      );
      localStorage.setItem('chatAgentWidgetUrl', data.chat_agent_widget_url);
      setAccessToken(data.access_token);
      return data.access_token;
    } catch {
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.fetchAccessTokenFailed),
          'error',
        ),
      );
      return null;
    }
  }, [accountSlug, dispatch, intl]);

  // A token that arrives after the user left must not open the setup in the
  // parent, which survives the navigation.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const openProgressor = useCallback(
    async (values: Partial<AxAgentValues> = {}) => {
      setIsLoading(true);
      const token = await loadSetupToken();
      if (!mountedRef.current) return;
      setIsLoading(false);
      if (!token) return;

      setAxAgentValues(prev => ({ ...prev, ...values }));
      setStartChatbotProgressor(true);
    },
    [loadSetupToken, setStartChatbotProgressor],
  );

  if (isLoading) return <Loader fullScreen size="2rem" />;

  // Opened only through openProgressor, which awaits the token first.
  if (startChatbotProgressor && accessToken) {
    return (
      <Grid container ref={measuredRef} className={classes.progressorWrapper}>
        <DisplayHeightContext.Provider value={displayHeight}>
          <ChatbotProgressor
            accessToken={accessToken}
            accountSlug={accountSlug}
            setStartChatbotProgressor={setStartChatbotProgressor}
            setAxAgentValues={setAxAgentValues}
            axAgentValues={axAgentValues}
          />
        </DisplayHeightContext.Provider>
      </Grid>
    );
  }

  if (agents.length > 0 || agentsIncomplete) {
    return (
      <AgentList
        openProgressor={openProgressor}
        loadSetupToken={loadSetupToken}
        setRefreshToken={setRefreshToken}
        axAgentValues={axAgentValues}
        setAgents={setAgents}
        agents={agents}
        agentsIncomplete={agentsIncomplete}
        accountSlug={accountSlug}
      />
    );
  }

  return (
    <SetupInstructions onStartChatbotProgressor={() => void openProgressor()} />
  );
};
